// Add/Edit Track - Form for creating and editing learning blocks
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, AlertCircle, CheckCircle, Info, BookOpen, Users, DollarSign, Image as ImageIcon, Upload } from 'lucide-react';
import blocksService from '../../services/blocksService';
import LearningBlockClassAssignment from './LearningBlockClassAssignment';
import LearningBlockEnrollments from './LearningBlockEnrollments';

const AddEditTrack = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const imageInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [blockData, setTrackData] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    competencyLevel: '',
    trackEmphasis: '',
    priceUpfront: '',
    priceMonthly: '',
    upfrontDiscountType: 'none',
    upfrontDiscountValue: '',
    monthlyDiscountType: 'none',
    monthlyDiscountValue: '',
    waiveUpfrontFees: false,
    waiveMonthlyFees: false,
    roundUpfrontPrice: false,
    roundMonthlyPrice: false,
    totalClassFees: 0,
    totalMaterialsFees: 0,
    numberOfMonths: 0,
    status: 'draft'
  });

  useEffect(() => {
    if (isEditMode) {
      fetchTrack();
    }
  }, [id]);

  // Refresh data when switching to pricing tab
  useEffect(() => {
    if (isEditMode && activeTab === 'pricing') {
      fetchTrack();
    }
  }, [activeTab]);

  const fetchTrack = async () => {
    try {
      setLoading(true);
      const data = await blocksService.getBlock(id);

      // Store full track data for child components
      setTrackData(data.block);

      // Calculate number of months from assigned classes
      let numberOfMonths = 0;

      if (data.classes && data.classes.length > 0) {
        // Calculate number of unique months
        const uniqueMonths = new Set(data.classes.map(c => c.monthNumber));
        numberOfMonths = uniqueMonths.size;
        console.log('Unique months:', Array.from(uniqueMonths));
        console.log('Number of months:', numberOfMonths);
      }

      // totalClassFees now contains the TOTAL materials fees
      const totalMaterialsFees = data.block.totalClassFees || 0;
      console.log('Total materials fees from database:', totalMaterialsFees);

      // Calculate monthly materials fee by dividing by number of months
      const monthlyMaterialsFee = numberOfMonths > 1
        ? Math.round(totalMaterialsFees / numberOfMonths)
        : totalMaterialsFees;

      console.log('Monthly materials fee (calculated):', monthlyMaterialsFee);

      setFormData({
        name: data.block.name || '',
        description: data.block.description || '',
        imageUrl: data.block.imageUrl || '',
        competencyLevel: data.block.competencyLevel || '',
        trackEmphasis: data.block.trackEmphasis || '',
        priceUpfront: data.block.priceUpfront || '',
        priceMonthly: data.block.priceMonthly || '',
        upfrontDiscountType: data.block.upfrontDiscountType || 'none',
        upfrontDiscountValue: data.block.upfrontDiscountValue || '',
        monthlyDiscountType: data.block.monthlyDiscountType || 'none',
        monthlyDiscountValue: data.block.monthlyDiscountValue || '',
        waiveUpfrontFees: data.block.waiveUpfrontFees || false,
        waiveMonthlyFees: data.block.waiveMonthlyFees || false,
        roundUpfrontPrice: data.block.roundUpfrontPrice || false,
        roundMonthlyPrice: data.block.roundMonthlyPrice || false,
        totalClassFees: monthlyMaterialsFee, // Monthly materials fee (calculated)
        totalMaterialsFees: totalMaterialsFees, // Total materials fees from database
        numberOfMonths: numberOfMonths,
        status: data.block.status || 'draft'
      });
    } catch (err) {
      console.error('Error fetching track:', err);
      setError('Failed to load track details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Simple client-side validation
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file size must be less than 10MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          imageUrl: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      imageUrl: ''
    }));
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const validateRequiredFields = () => {
    return !!(formData.name.trim());
  };

  const canAccessClassesTab = () => {
    return isEditMode || validateRequiredFields();
  };

  const canAccessPricingTab = () => {
    return isEditMode || validateRequiredFields();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Track name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const blockData = {
        ...formData,
        priceUpfront: parseFloat(formData.priceUpfront) || 0,
        priceMonthly: parseFloat(formData.priceMonthly) || 0,
        upfrontDiscountValue: parseFloat(formData.upfrontDiscountValue) || 0,
        monthlyDiscountValue: parseFloat(formData.monthlyDiscountValue) || 0,
        totalClassFees: parseFloat(formData.totalClassFees) || 0
      };

      let blockId;
      if (isEditMode) {
        await blocksService.updateBlock(id, blockData);
        blockId = id;
        setSuccess('Track saved successfully!');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const result = await blocksService.createBlock(blockData);
        blockId = result.block.id;
      }

      // If creating new track, navigate to classes tab in edit mode
      if (!isEditMode) {
        navigate(`/learning-blocks/edit/${blockId}`);
        // After navigation, set active tab to classes
        setTimeout(() => setActiveTab('classes'), 100);
      }
    } catch (err) {
      console.error('Error saving track:', err);
      setError('Failed to save block. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/learning-blocks')}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <BookOpen className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">
                {isEditMode ? 'Edit Block' : 'Add New Block'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {isEditMode ? 'Update block information and settings' : 'Create a new block program'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              form="track-form"
              type="submit"
              disabled={saving}
              className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-gray-100 px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 font-medium"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100"></div>
                  {isEditMode ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Block' : 'Save Block'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error {isEditMode ? 'updating' : 'creating'} block</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Success!</h3>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">{success}</p>
            </div>
            <button
              onClick={() => setSuccess('')}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'details'
                ? 'text-[#7A99D8] border-[#7A99D8] bg-blue-50 dark:bg-blue-900/20'
                : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Info className="w-4 h-4" />
            Block Details
          </button>
          <button
            type="button"
            onClick={() => canAccessClassesTab() && setActiveTab('classes')}
            disabled={!canAccessClassesTab()}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'classes'
                ? 'text-[#7A99D8] border-[#7A99D8] bg-blue-50 dark:bg-blue-900/20'
                : canAccessClassesTab()
                  ? 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                  : 'text-gray-400 dark:text-gray-600 border-transparent cursor-not-allowed'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Assign Classes
          </button>
          <button
            type="button"
            onClick={() => canAccessPricingTab() && setActiveTab('pricing')}
            disabled={!canAccessPricingTab()}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'pricing'
                ? 'text-[#7A99D8] border-[#7A99D8] bg-blue-50 dark:bg-blue-900/20'
                : canAccessPricingTab()
                  ? 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                  : 'text-gray-400 dark:text-gray-600 border-transparent cursor-not-allowed'
            }`}
          >
            <DollarSign className="w-4 h-4" />
            Pricing
          </button>
          {isEditMode && (
            <button
              type="button"
              onClick={() => setActiveTab('enrollments')}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'enrollments'
                  ? 'text-[#7A99D8] border-[#7A99D8] bg-blue-50 dark:bg-blue-900/20'
                  : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Users className="w-4 h-4" />
              Enrollments
            </button>
          )}
        </div>
      </div>

      {/* Tab Content - Classes */}
      {activeTab === 'classes' && isEditMode && (
        <div className="bg-white rounded-b-lg shadow-sm border border-t-0 border-gray-200 p-6">
          <LearningBlockClassAssignment blockId={id} track={blockData} />
        </div>
      )}

      {/* Tab Content - Enrollments */}
      {activeTab === 'enrollments' && isEditMode && (
        <div className="bg-white rounded-b-lg shadow-sm border border-t-0 border-gray-200 p-6">
          <LearningBlockEnrollments blockId={id} track={blockData} />
        </div>
      )}

      {/* Tab Content - Details */}
      {activeTab === 'details' && (
        <form id="track-form" onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6 rounded-b-lg border-t-0">
          {/* Basic Information */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>

            <div className="space-y-4">
              {/* Block Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Block Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="e.g., Fashion Seamstress Mastery"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Describe what students will learn in this block..."
                />
              </div>

              {/* Block Emphasis */}
              <div>
                <label htmlFor="trackEmphasis" className="block text-sm font-medium text-gray-700 mb-1">
                  Block Emphasis (Major/Focus Area)
                </label>
                <input
                  type="text"
                  id="trackEmphasis"
                  name="trackEmphasis"
                  value={formData.trackEmphasis}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="e.g., Fashion Seamstress, Home Decor, Quilting"
                />
              </div>

              {/* Competency Level */}
              <div>
                <label htmlFor="competencyLevel" className="block text-sm font-medium text-gray-700 mb-1">
                  Competency Level
                </label>
                <select
                  id="competencyLevel"
                  name="competencyLevel"
                  value={formData.competencyLevel}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  <option value="">Select level...</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            </div>
          </div>

          {/* Block Image */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Block Image</h2>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Upload an image that represents this block. This will be displayed on block cards and listings.
              </p>

              {!formData.imageUrl ? (
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-[#7A99D8] hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors">
                  <ImageIcon className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="bg-[#7A99D8] text-white px-6 py-2 rounded-lg hover:bg-[#6A89C8] transition-colors font-medium"
                    >
                      <Upload className="w-4 h-4 inline mr-2" />
                      Choose Image
                    </button>
                    <p className="text-sm text-gray-500 dark:text-gray-400">PNG, JPG, GIF up to 10MB</p>
                  </div>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="relative">
                  <div className="relative group">
                    <img
                      src={formData.imageUrl}
                      alt="Block image"
                      className="w-full h-64 object-cover rounded-lg shadow-sm"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-opacity flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-2">
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          className="bg-white text-gray-700 px-4 py-2 rounded-lg shadow-lg hover:bg-gray-50 transition-colors font-medium"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Status</h2>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Block Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="draft">Draft - Not visible to students</option>
                <option value="active">Active - Open for enrollment</option>
                <option value="inactive">Inactive - Closed for enrollment</option>
                <option value="archived">Archived - Hidden from main view</option>
              </select>
            </div>
          </div>

          {/* Spacer for sticky buttons */}
          <div className="h-20"></div>
        </form>
      )}

      {/* Tab Content - Pricing */}
      {activeTab === 'pricing' && (
        <div className="bg-white shadow-sm border border-gray-200 p-6 space-y-6 rounded-b-lg border-t-0">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Pricing</h2>

          {/* Two Column Layout */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column - Upfront Payment Pricing */}
            <div className="border border-gray-300 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
                Upfront Payment Pricing
              </h3>

              {/* Base Price */}
              <div>
                <label htmlFor="priceUpfront" className="block text-sm font-medium text-gray-700 mb-1">
                  Base Price
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    id="priceUpfront"
                    name="priceUpfront"
                    value={formData.priceUpfront}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Material Fees */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Material Fees
                </label>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    ${parseFloat(formData.totalMaterialsFees || 0).toFixed(2)}
                  </span>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="waiveUpfrontFees"
                      name="waiveUpfrontFees"
                      checked={formData.waiveUpfrontFees}
                      onChange={handleChange}
                      className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                    />
                    <label htmlFor="waiveUpfrontFees" className="ml-2 text-sm text-gray-700">
                      Waive Material Fees
                    </label>
                  </div>
                </div>
              </div>

              {/* Discount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount
                </label>
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <select
                    id="upfrontDiscountType"
                    name="upfrontDiscountType"
                    value={formData.upfrontDiscountType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  >
                    <option value="none">No Discount</option>
                    <option value="percentage">Percentage (%)</option>
                    <option value="dollar">Dollar Amount ($)</option>
                  </select>
                  <input
                    type="number"
                    id="upfrontDiscountValue"
                    name="upfrontDiscountValue"
                    value={formData.upfrontDiscountValue}
                    onChange={handleChange}
                    disabled={formData.upfrontDiscountType === 'none'}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Round to Nearest Dollar */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="roundUpfrontPrice"
                    name="roundUpfrontPrice"
                    checked={formData.roundUpfrontPrice}
                    onChange={handleChange}
                    className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                  />
                  <label htmlFor="roundUpfrontPrice" className="ml-2 text-sm text-gray-700">
                    Round total to nearest dollar
                  </label>
                </div>
              </div>

              {/* Upfront Total */}
              <div className="pt-4 mt-4 border-t-2 border-gray-300">
                {(() => {
                  const baseTotal = parseFloat(formData.priceUpfront || 0);
                  const materialsFees = formData.waiveUpfrontFees ? 0 : parseFloat(formData.totalMaterialsFees || 0);

                  let discount = 0;
                  if (formData.upfrontDiscountType === 'percentage') {
                    discount = (baseTotal + materialsFees) * (parseFloat(formData.upfrontDiscountValue || 0) / 100);
                  } else if (formData.upfrontDiscountType === 'dollar') {
                    discount = parseFloat(formData.upfrontDiscountValue || 0);
                  }

                  let total = baseTotal + materialsFees - discount;

                  // Apply rounding if enabled
                  if (formData.roundUpfrontPrice) {
                    total = Math.round(total);
                  }

                  return (
                    <>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                        <span>Base Price:</span>
                        <span>${baseTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                        <span>Material Fees:</span>
                        <span>${materialsFees.toFixed(2)}</span>
                      </div>
                      {discount > 0 && (
                        <div className="flex items-center justify-between text-sm text-green-600 mb-1">
                          <span>Discount:</span>
                          <span>-${discount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-lg font-bold text-gray-900 mt-3 pt-3 border-t border-gray-200">
                        <span>Total:</span>
                        <span>${formData.roundUpfrontPrice ? total.toFixed(0) : total.toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Right Column - Monthly Payment Pricing */}
            <div className="border border-gray-300 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
                Monthly Payment Pricing
              </h3>

              {/* Base Price */}
              <div>
                <label htmlFor="priceMonthly" className="block text-sm font-medium text-gray-700 mb-1">
                  Base Price (Per Month)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    id="priceMonthly"
                    name="priceMonthly"
                    value={formData.priceMonthly}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Default is average of all classes in block
                </p>
              </div>

              {/* Material Fees */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Material Fees (Per Month)
                </label>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    ${parseFloat(formData.totalClassFees || 0).toFixed(2)}
                  </span>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="waiveMonthlyFees"
                      name="waiveMonthlyFees"
                      checked={formData.waiveMonthlyFees}
                      onChange={handleChange}
                      className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                    />
                    <label htmlFor="waiveMonthlyFees" className="ml-2 text-sm text-gray-700">
                      Waive Material Fees
                    </label>
                  </div>
                </div>
              </div>

              {/* Discount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount
                </label>
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <select
                    id="monthlyDiscountType"
                    name="monthlyDiscountType"
                    value={formData.monthlyDiscountType || 'none'}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  >
                    <option value="none">No Discount</option>
                    <option value="percentage">Percentage (%)</option>
                    <option value="dollar">Dollar Amount ($)</option>
                  </select>
                  <input
                    type="number"
                    id="monthlyDiscountValue"
                    name="monthlyDiscountValue"
                    value={formData.monthlyDiscountValue || ''}
                    onChange={handleChange}
                    disabled={!formData.monthlyDiscountType || formData.monthlyDiscountType === 'none'}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Round to Nearest Dollar */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="roundMonthlyPrice"
                    name="roundMonthlyPrice"
                    checked={formData.roundMonthlyPrice}
                    onChange={handleChange}
                    className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                  />
                  <label htmlFor="roundMonthlyPrice" className="ml-2 text-sm text-gray-700">
                    Round total to nearest dollar
                  </label>
                </div>
              </div>

              {/* Monthly Total */}
              <div className="pt-4 mt-4 border-t-2 border-gray-300">
                {(() => {
                  const baseTotal = parseFloat(formData.priceMonthly || 0);
                  const materialsFees = formData.waiveMonthlyFees ? 0 : parseFloat(formData.totalClassFees || 0);

                  let discount = 0;
                  if (formData.monthlyDiscountType === 'percentage') {
                    discount = (baseTotal + materialsFees) * (parseFloat(formData.monthlyDiscountValue || 0) / 100);
                  } else if (formData.monthlyDiscountType === 'dollar') {
                    discount = parseFloat(formData.monthlyDiscountValue || 0);
                  }

                  let total = baseTotal + materialsFees - discount;

                  // Apply rounding if enabled
                  if (formData.roundMonthlyPrice) {
                    total = Math.round(total);
                  }

                  return (
                    <>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                        <span>Base Price:</span>
                        <span>${baseTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                        <span>Material Fees:</span>
                        <span>${materialsFees.toFixed(2)}</span>
                      </div>
                      {discount > 0 && (
                        <div className="flex items-center justify-between text-sm text-green-600 mb-1">
                          <span>Discount:</span>
                          <span>-${discount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-lg font-bold text-gray-900 mt-3 pt-3 border-t border-gray-200">
                        <span>Total Per Month:</span>
                        <span>${formData.roundMonthlyPrice ? total.toFixed(0) : total.toFixed(2)}/mo</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Back to Details
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 disabled:bg-pink-300 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Pricing'}
            </button>
          </div>
        </div>
      )}

      {/* Sticky Form Actions - Only show on details tab */}
      {activeTab === 'details' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/learning-blocks')}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="track-form"
              disabled={saving}
              className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 disabled:bg-pink-300 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              {isEditMode ? (
                <>
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </>
              ) : (
                <>
                  <BookOpen className="w-4 h-4" />
                  {saving ? 'Creating...' : 'Select Classes'}
                </>
              )}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AddEditTrack;
